<template>
  <swiper-base
    ref="swiper"
    class="products-catalog"
    :items="catalogProductNames"
    :last-item-offset="lastItemOffset"
    @change="onChange"
  >
    <template v-slot:item="{ item }">
      <app-product :product-name="item" image-lazy />
    </template>

    <template v-slot:buttons>
      <u-icon-button
        v-if="hasPrevButton"
        class="products-catalog__button products-catalog__button--prev gte-600"
        icon-name="left"
        size="m"
        @click="onSwipe('end')"
      />
      <u-icon-button
        v-if="hasNextButton"
        class="products-catalog__button products-catalog__button--next gte-600"
        icon-name="right"
        size="m"
        @click="onSwipe('start')"
      />
    </template>
  </swiper-base>
</template>

<script>
/* eslint-disable */
import { mapGetters } from 'vuex';

import { UIconButton } from 'universkin-shared';

import SwiperBase from '@/components/common/app-swiper/SwiperBase.vue';
import AppProduct from '@/components/common/AppProduct.vue';
import { getProductPackStockQuantity, isProductPackAvailable } from '@/models/core/ProductPack';
import { getProductOptionsDefaultOption } from '@/models/core/ProductOptions';

import { getRoutineProductNamesPerSubsectionByCountry } from '@/business-logic/yourRoutineSection';

import { types } from '@/store/types';
import { PRODUCT_NAMES } from '@/constants/treatment';
import { COUNTRY_ISO } from '@/constants/countries';

const ORDERED_PRODUCT_LIST = [
  PRODUCT_NAMES.FACE_SERUM,
  PRODUCT_NAMES.EYE_CONTOUR_SERUM,
  PRODUCT_NAMES.NEXULTRA_O,
  PRODUCT_NAMES.GEL,
  PRODUCT_NAMES.UNIVERSKIN_H,
  PRODUCT_NAMES.NEXULTRA_P_LIGHT,
  PRODUCT_NAMES.NEXULTRA_P_RICH,
  PRODUCT_NAMES.NEXULTRA_B,
  PRODUCT_NAMES.NEXULTRA_UV,
  PRODUCT_NAMES.SUNSCREEN_SPF,
  PRODUCT_NAMES.SUNSCREEN_CRM,
  PRODUCT_NAMES.SUNSCREEN_FLD,
  PRODUCT_NAMES.NEXULTRA_1,
  PRODUCT_NAMES.NEXULTRA_2,
  PRODUCT_NAMES.NEXULTRA_3,
  PRODUCT_NAMES.UNIVERSKIN_M
];

const COUNTRIES_USCA_UNAVAILABLE_UNIVERSKIN_UV = [
  COUNTRY_ISO.CANADA,
  COUNTRY_ISO.UNITED_STATES
];
const COUNTRIES_NZAU_UNAVAILABLE_UNIVERSKIN_UV = [
  COUNTRY_ISO.NEW_ZEELAND,
  COUNTRY_ISO.AUSTRALIA
];
export default {
  name: 'ProductsCatalog',
  components: { SwiperBase, UIconButton, AppProduct },
  props: {
    productPageName: {
      type: String,
      required: true
    }
  },
  data() {
    return {
      currentItemIndex: 0
    };
  },
  computed: {
    ...mapGetters({
      allProductOptions: types.getters.ALL_PRODUCT_OPTIONS_SETS,
      // isProductInCart: types.getters.GET_IS_PRODUCT_ADDED_TO_CART_BY_NAME,
      deliveryCountry: types.getters.DELIVERY_COUNTRY,
      getIsProductRecommended: types.getters.GET_IS_PRODUCT_RECOMMENDED
    }),
    catalogProductNames() {
      const productNames = ORDERED_PRODUCT_LIST.filter(name => {
        const productPack = getProductOptionsDefaultOption(this.allProductOptions[name]);

        const isProductAvailable =
          productPack &&
          isProductPackAvailable(productPack) &&
          getProductPackStockQuantity(productPack) &&
          this.productsPerCountry.includes(name);
        if (COUNTRIES_USCA_UNAVAILABLE_UNIVERSKIN_UV.includes(this.deliveryCountry)) {
          return name === PRODUCT_NAMES.SUNSCREEN_SPF || name === PRODUCT_NAMES.SUNSCREEN_CRM;
        }
        if (COUNTRIES_NZAU_UNAVAILABLE_UNIVERSKIN_UV.includes(this.deliveryCountry)) {
          return name === PRODUCT_NAMES.SUNSCREEN_SPF || name === PRODUCT_NAMES.SUNSCREEN_CRM;
        }

        const isAllowedProductName =
          name !== this.productPageName && name !== PRODUCT_NAMES.FREE_SAMPLE;
        const isFaceSerumProduct = name === PRODUCT_NAMES.FACE_SERUM;
        const isFaceSerumRecommended = this.getIsProductRecommended(name);

        const isAllowedProduct = isAllowedProductName && isProductAvailable;
        return isFaceSerumProduct ? isFaceSerumRecommended && isAllowedProduct : isAllowedProduct;
      });

      return productNames;
    },
    productsPerCountry() {
      return Object.values(
        getRoutineProductNamesPerSubsectionByCountry(this.deliveryCountry)
      ).flat();
    },
    hasPrevButton() {
      return this.currentItemIndex > 0;
    },
    hasNextButton() {
      return this.currentItemIndex < this.catalogProductNames.length - 1 - this.lastItemOffset;
    },
    lastItemOffset() {
      const windowWidth = this.$windowWidth.value;

      if (windowWidth >= 1440) {
        return 3;
      }

      if (windowWidth >= 960) {
        return 2;
      }

      if (windowWidth >= 600) {
        return 1;
      }

      return 0;
    }
  },
  methods: {
    onSwipe(direction) {
      this.$refs.swiper.swipeInDirection(direction);
    },
    onChange(index) {
      this.currentItemIndex = index;
    }
  }
};
</script>

<style lang="scss" scoped>
.products-catalog {
  --item-width: 62vw;
  --item-gap: 16px;
  position: relative;

  &__button {
    position: absolute;
    top: 40%;

    &:lang(ar) {
      transform: scaleX(-1);
    }

    &--prev:not(:lang(ar)),
    &--next:lang(ar) {
      left: 0;
    }

    &--next:not(:lang(ar)),
    &--prev:lang(ar) {
      right: 0;
    }
  }
}

@media (min-width: 600px) {
  .products-catalog {
    --item-width: 321px;
    --item-gap: 24px;
  }
}

@media (min-width: 960px) {
  .products-catalog {
    &__button {
      &--prev:not(:lang(ar)),
      &--next:lang(ar) {
        left: -16px;
      }

      &--next:not(:lang(ar)),
      &--prev:lang(ar) {
        right: -16px;
      }
    }
  }
}
</style>
